import { Stack, Typography } from '@mui/material';
import { Assessment } from 'types/analysis-result.types';
import { Scores } from 'features/augmented-writing/augmented-writing.types';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import MatchOverall from './match-overall';
import AnalysisDetails from './analysis-details';
import UserDone from './user-done';

interface Props {
  scores: Scores | undefined;
  userAssessment: Assessment | undefined;
  contactAssessmentName: string | undefined;
  loading: boolean;
  clickedChartCategoryKey: string | undefined;
  userDone: boolean;
  profileName: string | undefined;
  /** 'default' keeps the legacy column, 'panel' renders the two-zone panel design */
  variant?: 'default' | 'panel';

  onChartCategoryClicked(category: any): void;
}

const AnalysisResults: React.FC<Props> = ({
  scores,
  userAssessment,
  contactAssessmentName,
  loading,
  clickedChartCategoryKey,
  userDone,
  profileName,
  variant = 'default',
  onChartCategoryClicked,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const isPanel = variant === 'panel';

  return (
    <div className={isPanel ? classes.panelResults : classes.results}>
      {!isPanel ? <Typography className={classes.title}>{t('result')}</Typography> : null}
      {loading ? <PaiDotLoader size="normal" /> : null}
      {!loading && !_.isNil(scores) ? (
        <Stack direction={'column'} gap={isPanel ? '16px' : '8px'}>
          {!userDone ? (
            <MatchOverall score={scores.overall} variant={variant} />
          ) : (
            <UserDone contactAssessmentName={contactAssessmentName} profileName={profileName} />
          )}
          <AnalysisDetails
            scores={scores}
            userAssessment={userAssessment}
            clickedChartCategoryKey={clickedChartCategoryKey}
            onChartCategoryClicked={onChartCategoryClicked}
            userDone={userDone}
            variant={variant}
          />
        </Stack>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  results: {
    padding: '16px',
    [theme.breakpoints.down('xl')]: {
      padding: '16px 8px',
    },
    [theme.breakpoints.down('lg')]: {
      padding: '16px',
    },
  },
  panelResults: {
    padding: '20px',
    [theme.breakpoints.down('lg')]: {
      padding: '16px',
    },
  },
  title: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    textAlign: 'center',
    marginBottom: '16px',
  },
}));

export default AnalysisResults;
